import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import {
  SegmentedToggle,
  ToggleRow,
  TWButton,
  TWInput,
  TWModal,
  TWSelect,
} from '../../../components/ui';
import { SkeletonBlock } from '../../../components/skeleton/skeleton';
import fleetApi from '../../../services/fleetApi';
import { fleetErrorText } from '../use-fleet-poll';
import {
  APPS,
  ROLLOUT_DEFAULTS,
  buildDeviceListQuery,
  needsTypedConfirm,
  offlineHoldText,
  rolloutRoleFor,
  rolloutSummary,
} from '../fleet-utils';
import CompanyStoreSelect from './company-store-select';

const PICKER_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;
const BATCH_MAX = 300;
const INT_MAX = 2147483647;
const WINDOW_HINT = 'Installs are sent only inside this window (store time).';
const WINDOW_ERROR = 'Set both window times or neither';

const appLabel = (app) => APPS.find((a) => a.value === app)?.label || app;

// A whole number inside [min, max], else null.
const parseWhole = (text, min, max) => {
  const trimmed = String(text).trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= min && n <= max ? n : null;
};

// Release -> target -> batch settings -> a server-counted summary, then Create.
// The summary is the P-1 safety: nobody confirms a fleet-wide install without
// seeing how many devices it reaches.
const RolloutCreateModal = ({ open, onClose, onCreated, initialDeviceIds }) => {
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState(undefined);
  const [kind, setKind] = useState('role');
  const [companyId, setCompanyId] = useState('');
  const [storeIds, setStoreIds] = useState([]);
  const [deviceIds, setDeviceIds] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [batchSize, setBatchSize] = useState(String(ROLLOUT_DEFAULTS.batchsize));
  const [interval, setIntervalText] = useState(String(ROLLOUT_DEFAULTS.batchintervalminutes));
  const [maxFailures, setMaxFailures] = useState(String(ROLLOUT_DEFAULTS.maxfailures));
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [replace, setReplace] = useState(false);
  const [preview, setPreview] = useState({ status: 'idle', count: 0, error: '' });
  const [submitting, setSubmitting] = useState(false);
  // Spec 5.2: a rollout that reaches more than 10 devices needs the count typed.
  const [typedCount, setTypedCount] = useState('');
  // False from close until the opening reset has run: the effects below must
  // not act on the previous session's release and target.
  const [ready, setReady] = useState(false);
  const [devicesCleared, setDevicesCleared] = useState(false);

  const initialRef = useRef(initialDeviceIds);
  initialRef.current = initialDeviceIds;
  const previewRequest = useRef(0);
  const submitRef = useRef(false);
  // Names of every device the picker has shown, so a selection made under one
  // search keeps its name under the next.
  const deviceLabels = useRef({});

  // Each opening starts clean; the ids handed in by the Devices list are
  // already selected, whether or not they appear on the picker's page.
  useEffect(() => {
    if (!open) {
      setReady(false);
      return undefined;
    }
    const ids = Array.isArray(initialRef.current) ? initialRef.current : [];
    setDevicesCleared(false);
    setReleaseId(undefined);
    setKind(ids.length > 0 ? 'devices' : 'role');
    setCompanyId('');
    setStoreIds([]);
    setDeviceIds(ids);
    setSearch('');
    setDebouncedSearch('');
    setBatchSize(String(ROLLOUT_DEFAULTS.batchsize));
    setIntervalText(String(ROLLOUT_DEFAULTS.batchintervalminutes));
    setMaxFailures(String(ROLLOUT_DEFAULTS.maxfailures));
    setWindowStart('');
    setWindowEnd('');
    setReplace(false);
    setPreview({ status: 'idle', count: 0, error: '' });
    setTypedCount('');
    setReady(true);

    let cancelled = false;
    fleetApi
      .listReleases()
      .then((res) => {
        if (!cancelled) setReleases(res.data.data || []);
      })
      .catch((err) => {
        if (!cancelled) message.error(fleetErrorText(err, 'Could not load releases'));
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const release = releases.find((r) => r.id === releaseId);
  const role = release ? rolloutRoleFor(release.app) : 'all';
  // Offered once a release is chosen; the API never replaces the agent itself.
  const replaceApplies = Boolean(release) && release.app !== 'agent';

  const target = useMemo(() => {
    if (kind === 'role') return release ? { kind: 'role', role } : null;
    if (kind === 'stores') return storeIds.length > 0 ? { kind: 'stores', storeids: storeIds } : null;
    return deviceIds.length > 0 ? { kind: 'devices', deviceids: deviceIds } : null;
  }, [kind, release, role, storeIds, deviceIds]);
  const targetKey = JSON.stringify(target);

  // One preview per change of release or target. The counter drops an answer
  // that arrives after a newer request, so the summary never shows a stale count.
  useEffect(() => {
    previewRequest.current += 1;
    const mine = previewRequest.current;
    // A new release or target is a new count: the typed one never carries over.
    setTypedCount('');
    if (!open || !ready || !release || !target) {
      setPreview({ status: 'idle', count: 0, error: '' });
      return undefined;
    }
    setPreview({ status: 'pending', count: 0, error: '' });
    fleetApi
      .previewRollout({ releaseid: release.id, target })
      .then((res) => {
        if (mine !== previewRequest.current) return;
        setPreview({ status: 'ok', count: Number(res.data.data.devicecount) || 0, error: '' });
      })
      .catch((err) => {
        if (mine !== previewRequest.current) return;
        setPreview({
          status: 'error',
          count: 0,
          error: fleetErrorText(err, 'Could not count the devices'),
        });
      });
    return () => {
      previewRequest.current += 1;
    };
    // `release` and `target` are read through releaseId and targetKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ready, releaseId, targetKey]);

  // The device picker: the release's role decides the query (no role for an
  // agent release), the first 100 matches only.
  useEffect(() => {
    if (!open || !ready || kind !== 'devices') return undefined;
    let cancelled = false;
    fleetApi
      .listDevices(
        buildDeviceListQuery({ role, search: debouncedSearch, page: 1, pageSize: PICKER_PAGE_SIZE }),
      )
      .then((res) => {
        if (cancelled) return;
        const list = res.data.data || [];
        list.forEach((d) => {
          deviceLabels.current[d.id] = d.name || d.serialnumber || d.id;
        });
        setPickerRows(list);
        setPickerTotal(res.data.totalRecords ?? list.length);
      })
      .catch((err) => {
        if (!cancelled) message.error(fleetErrorText(err, 'Could not load devices'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, ready, kind, role, debouncedSearch]);

  const deviceOptions = useMemo(() => {
    const shown = new Set(pickerRows.map((d) => d.id));
    const options = pickerRows.map((d) => ({
      value: d.id,
      label: d.name || d.serialnumber || d.id,
    }));
    deviceIds
      .filter((id) => !shown.has(id))
      .forEach((id) =>
        options.push({ value: id, label: deviceLabels.current[id] || 'Device ' + id.slice(0, 8) }),
      );
    return options;
  }, [pickerRows, deviceIds]);

  const releaseOptions = releases.map((r) => ({
    value: r.id,
    label: `${appLabel(r.app)} ${r.versionname} (${r.versioncode})`,
  }));

  const targetOptions = [
    { value: 'role', label: role === 'all' ? 'All devices' : `All ${role} devices` },
    { value: 'stores', label: 'Selected stores' },
    { value: 'devices', label: 'Selected devices' },
  ];

  const batchsize = parseWhole(batchSize, 1, BATCH_MAX);
  const batchintervalminutes = parseWhole(interval, 0, INT_MAX);
  const maxfailures = parseWhole(maxFailures, 0, INT_MAX);
  const settingsValid =
    batchsize !== null && batchintervalminutes !== null && maxfailures !== null;
  const windowHalf = Boolean(windowStart) !== Boolean(windowEnd);

  const typedRequired = preview.status === 'ok' && needsTypedConfirm(preview.count);
  const typedOk = !typedRequired || typedCount === String(preview.count);

  const canCreate =
    Boolean(release) &&
    preview.status === 'ok' &&
    preview.count > 0 &&
    typedOk &&
    settingsValid &&
    !windowHalf &&
    !submitting;

  // Devices picked under one role do not carry over to a release of another
  // role: the picker lists a different set, so the pick is cleared and the
  // modal says so.
  const changeRelease = (nextId) => {
    const next = releases.find((r) => r.id === nextId);
    if (release && next && rolloutRoleFor(next.app) !== role && deviceIds.length > 0) {
      setDeviceIds([]);
      setDevicesCleared(true);
    }
    setReleaseId(nextId);
  };

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    if (!canCreate || submitRef.current) return;
    submitRef.current = true;
    setSubmitting(true);
    const body = {
      releaseid: release.id,
      target,
      batchsize,
      batchintervalminutes,
      maxfailures,
      setscurrent: true,
    };
    if (windowStart && windowEnd) {
      body.windowstart = windowStart;
      body.windowend = windowEnd;
    }
    // Sent only when on: an old API never sees the key.
    if (replace && replaceApplies) body.replace = true;
    try {
      const res = await fleetApi.createRollout(body);
      message.success('Rollout created');
      onCreated?.(res.data.data);
    } catch (err) {
      message.error(fleetErrorText(err, 'Could not create the rollout'));
    } finally {
      submitRef.current = false;
      setSubmitting(false);
    }
  };

  let summary;
  if (preview.status === 'pending') {
    summary = <SkeletonBlock className="h-5 w-64" />;
  } else if (preview.status === 'error') {
    summary = (
      <p role="alert" className="text-sm font-semibold text-rose-600 dark:text-rose-400">
        {preview.error}
      </p>
    );
  } else if (preview.status === 'ok' && settingsValid) {
    summary = (
      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {rolloutSummary(preview.count, batchsize, batchintervalminutes).text}
      </p>
    );
  } else if (preview.status === 'ok') {
    summary = (
      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
        Enter valid batch settings to see the plan.
      </p>
    );
  } else {
    summary = (
      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
        Choose a release and a target to see how many devices it reaches.
      </p>
    );
  }

  return (
    <TWModal
      open={open}
      onClose={close}
      dismissible={!submitting}
      size="2xl"
      title="New rollout"
      footer={
        <div className="flex flex-row-reverse gap-2">
          <TWButton variant="primary" loading={submitting} disabled={!canCreate} onClick={submit}>
            Create rollout
          </TWButton>
          <TWButton variant="default" disabled={submitting} onClick={close}>
            Cancel
          </TWButton>
        </div>
      }
    >
      <div className="space-y-5">
        <TWSelect
          label="Release"
          placeholder="Choose a release"
          options={releaseOptions}
          value={releaseId}
          allowClear={false}
          disabled={submitting}
          onChange={changeRelease}
        />

        <div className="space-y-3">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Target</p>
          <div className="overflow-x-auto">
            <SegmentedToggle
              ariaLabel="Target"
              value={kind}
              onChange={setKind}
              options={targetOptions}
            />
          </div>
          {kind === 'stores' ? (
            <CompanyStoreSelect
              companyId={companyId}
              storeId={storeIds}
              storeMode="multiple"
              labels={{ store: 'Stores' }}
              placeholders={{ store: 'Choose stores' }}
              onChange={({ companyid, storeid }) => {
                setCompanyId(companyid || '');
                setStoreIds(Array.isArray(storeid) ? storeid : []);
              }}
            />
          ) : null}
          {kind === 'devices' ? (
            <div className="space-y-2">
              <TWInput
                label="Search devices"
                placeholder="Name, serial, store or company"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <TWSelect
                label="Devices"
                mode="multiple"
                maxTagCount={3}
                placeholder="Choose devices"
                options={deviceOptions}
                value={deviceIds}
                disabled={submitting}
                onChange={(value) => setDeviceIds(value || [])}
              />
              {devicesCleared && deviceIds.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Picked devices were cleared because the release targets another role.
                </p>
              ) : null}
              {pickerTotal > PICKER_PAGE_SIZE ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Showing the first 100 — search to narrow
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <TWInput
            label="Batch size"
            type="number"
            inputMode="numeric"
            min="1"
            max={String(BATCH_MAX)}
            step="1"
            value={batchSize}
            error={batchsize === null ? 'Enter 1 to 300' : undefined}
            disabled={submitting}
            onChange={(e) => setBatchSize(e.target.value)}
          />
          <TWInput
            label="Minutes between batches"
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={interval}
            error={batchintervalminutes === null ? 'Enter 0 or more' : undefined}
            disabled={submitting}
            onChange={(e) => setIntervalText(e.target.value)}
          />
          <TWInput
            label="Failures before pause"
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            value={maxFailures}
            error={maxfailures === null ? 'Enter 0 or more' : undefined}
            disabled={submitting}
            onChange={(e) => setMaxFailures(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <TWInput
              label="Window start"
              type="time"
              value={windowStart}
              disabled={submitting}
              onChange={(e) => setWindowStart(e.target.value)}
            />
            <TWInput
              label="Window end"
              type="time"
              value={windowEnd}
              disabled={submitting}
              onChange={(e) => setWindowEnd(e.target.value)}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{WINDOW_HINT}</p>
          {windowHalf ? (
            <p className="text-xs text-rose-600 dark:text-rose-400">{WINDOW_ERROR}</p>
          ) : null}
        </div>

        {replaceApplies ? (
          <ToggleRow
            title="Replace if the installed app's signature differs"
            description="Loses the app's data; the kiosk must be paired again."
            enabled={replace}
            onChange={setReplace}
            disabled={submitting}
          />
        ) : null}

        <div className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
          {summary}
          {batchintervalminutes !== null ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {offlineHoldText(batchintervalminutes)}
            </p>
          ) : null}
        </div>

        {typedRequired ? (
          <TWInput
            label={`Type ${preview.count} to confirm`}
            hint={`This rollout reaches ${preview.count} devices.`}
            value={typedCount}
            disabled={submitting}
            onChange={(e) => setTypedCount(e.target.value)}
            autoComplete="off"
            inputMode="numeric"
          />
        ) : null}
      </div>
    </TWModal>
  );
};

export default RolloutCreateModal;
